import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from '../../api/axiosInstance';
import { useSocket } from "../../context/SocketContext";
import { useAuth } from "../../context/AuthContext";
import Peer from "simple-peer";
import ActiveCall from "./ActiveCall";

export default function CallScreen() {
  const { peerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { socket } = useSocket();
  const { userId } = useAuth();
  const initialVideo = !!location.state?.video;
  const chatId = location.state?.chatId;

  const initialPeer = location.state?.peer || {
    id: peerId,
    name: "Calling...",
    image: null,
  };

  const [peerInfo, setPeerInfo] = useState(initialPeer);
  const [status, setStatus] = useState("dialing"); // dialing | connected | ended | rejected
  const [callStartTs, setCallStartTs] = useState(null);
  const callStartRef = useRef(null);

  const localStream = useRef(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const peers = useRef({});
  const endedRef = useRef(false);
  const iceConfigRef = useRef({});
  // Track applied remote signals to avoid duplicate application (which breaks WebRTC state machine)
  const appliedSignalsRef = useRef({}); // { [remoteId]: Set<string> }
  const [localFacing, setLocalFacing] = useState('user'); // 'user' | 'environment'

  const swapCamera = async () => {
    try {
      if (!localStream.current) return;
      const oldVideoTrack = localStream.current.getVideoTracks()[0];
      if (!oldVideoTrack) return;

  const settings = typeof oldVideoTrack.getSettings === 'function' ? oldVideoTrack.getSettings() : {};
  const currentFacing = settings.facingMode || (oldVideoTrack.label?.toLowerCase().includes('back') ? 'environment' : 'user');
      const targetFacing = currentFacing === 'environment' ? 'user' : 'environment';

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: targetFacing } },
        audio: false,
      });
      const newVideoTrack = newStream.getVideoTracks()[0];
      if (!newVideoTrack) {
        newStream.getTracks().forEach(t => t.stop());
        return;
      }

      // Replace track in all active peers
      Object.values(peers.current).forEach((p) => {
        try {
          if (typeof p.replaceTrack === 'function') {
            p.replaceTrack(oldVideoTrack, newVideoTrack, localStream.current);
          } else if (p._pc?.getSenders) {
            const sender = p._pc.getSenders().find(s => s.track === oldVideoTrack);
            if (sender) sender.replaceTrack(newVideoTrack).catch(() => {});
          }
        } catch (e) {
          console.warn('replaceTrack failed, will rely on stream update', e);
        }
      });

      // Update local stream contents for local preview and any late addTrack
      try {
        localStream.current.removeTrack(oldVideoTrack);
      } catch {}
      localStream.current.addTrack(newVideoTrack);

      // Stop the old track after replacement
      try { oldVideoTrack.stop(); } catch {}

      // Do not stop newVideoTrack; it must stay live. Avoid leaking extra tracks in newStream.
      newStream.getAudioTracks?.().forEach(t => t.stop());
      // Keep newStream object around implicitly via newVideoTrack.

      setLocalFacing(targetFacing);
    } catch (err) {
      console.error('Failed to swap camera', err);
    }
  };

  useEffect(() => {
    callStartRef.current = callStartTs;
  }, [callStartTs]);

  const cleanupConnections = () => {
    try {
      Object.values(peers.current).forEach((p) => {
        try { p.destroy(); } catch (e) {}
      });
      peers.current = {};
      if (localStream.current) {
        localStream.current.getTracks().forEach((t) => {
          try { t.stop(); } catch (e) {}
        });
      }
    } catch (e) {
      console.error('Cleanup error', e);
    }
  };

  const endCall = async (notifyPeer = true) => {
    if (endedRef.current) return;
    endedRef.current = true;

    if (socket && peerInfo?.id && notifyPeer) {
      socket.emit("cancel-call", { to: peerInfo.id });
      socket.emit("reject-call", { to: peerInfo.id });
    }
    cleanupConnections();

    const wasConnected = !!callStartRef.current;

    if (chatId) {
      try {
        if (wasConnected) {
          await axiosInstance.post(`/chat/call-event/${chatId}`, {
            status: "ended",
            duration: Math.floor((Date.now() - callStartRef.current) / 1000),
            callType: initialVideo ? "video call" : "audio call"
          });
        }
        else {
          await axiosInstance.post(`/chat/call-event/${chatId}`, {
            status: "missed",
            callType: initialVideo ? "video call" : "audio call"
          });
        }
      } catch (e) {
        console.error("Failed to save call event", e);
      }
    }

    setStatus(wasConnected ? "ended" : "rejected");
    navigate(-1);
  };

  const fetchIceConfig = async () => {
    try {
      const res = await axiosInstance.get('/ice'); // axiosInstance is configured with /api base
      console.log('Fetched ICE config:', res.data);
      const { iceServers, iceTransportPolicy } = res.data || {};
      iceConfigRef.current = {
        config: { iceServers: iceServers || [] },
        // simple-peer supports RTCPeerConnection config keys at top-level via 'config'
        // It doesn't accept iceTransportPolicy directly; we pass through in config if supported by browser
        transportPolicy: iceTransportPolicy || 'all',
      };
    } catch (e) {
      console.warn('Failed to fetch ICE config, falling back to public STUN', e);
      iceConfigRef.current = {
        config: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] },
        transportPolicy: 'all',
      };
    }
  };

  const startWebRTC = async (initiator, remoteId, incomingSignal = null) => {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      if (!initialVideo) {
        stream.getVideoTracks().forEach(t => (t.enabled = false));
      }
      localStream.current = stream;
      // Establish initial facing mode for proper mirroring of local preview
      const vTrack = stream.getVideoTracks()[0];
      if (vTrack) {
        const s = typeof vTrack.getSettings === 'function' ? vTrack.getSettings() : {};
        const initialFacing = s.facingMode || (vTrack.label?.toLowerCase().includes('back') ? 'environment' : 'user');
        setLocalFacing(initialFacing === 'environment' ? 'environment' : 'user');
      } else {
        setLocalFacing('user');
      }
    } catch (e) {
      console.error("Failed to get user media:", e);
      return;
    }

    if (!iceConfigRef.current.config) {
      await fetchIceConfig();
    }

    const rtcConfig = {
      ...iceConfigRef.current,
    };

    const peer = new Peer({
      initiator,
      trickle: false,
      stream,
      config: {
        ...(rtcConfig.config || {}),
        // Optionally force TURN-only on iOS/Android devices that struggle with P2P
        // Many mobile networks block UDP; forcing relay helps
        iceTransportPolicy: iceConfigRef.current.transportPolicy || 'all',
      },
    });

    peer.on("signal", (signal) => {
      socket.emit("send-signal", { to: remoteId, signal });
    });

    peer.on("stream", (remoteStream) => {
      setRemoteStream(remoteStream);
    });

    peer.on("error", (err) => console.error("Peer error:", err));
    peer.on("close", () => endCall(false));

    peers.current[remoteId] = peer;

    if (incomingSignal) {
      peer.signal(incomingSignal);
    }
  };

  useEffect(() => {
    if (!socket || !peerInfo?.id) return;

    socket.emit("call-user", { to: peerInfo.id, from: userId });
    startWebRTC(true, peerInfo.id);
    setStatus("dialing");

    const onReceiveSignal = ({ from, signal }) => {
      try {
        console.log("[client] receive-signal", { from, signal });
        const peer = peers.current[from];
        if (!peer) return;

        const sigType = signal?.type || (signal?.candidate ? 'candidate' : 'unknown');
        const sigPayload = signal?.sdp || signal?.candidate || JSON.stringify(signal || {});
        const sigKey = `${sigType}:${typeof sigPayload === 'string' ? sigPayload : JSON.stringify(sigPayload)}`;

        if (!appliedSignalsRef.current[from]) appliedSignalsRef.current[from] = new Set();
        if (appliedSignalsRef.current[from].has(sigKey)) {
          return;
        }

        const signalingState = (peer._pc && peer._pc.signalingState) || null;
        if (sigType === 'answer' && signalingState === 'stable') {
          return;
        }

        peer.signal(signal);
        appliedSignalsRef.current[from].add(sigKey);
      } catch (err) {
        console.error('[client] onReceiveSignal error', err);
      }
    };

    const onAccepted = ({ from }) => {
      if (from === peerInfo.id || from === userId) {
        setStatus("connected");
        setCallStartTs(Date.now());
        callStartRef.current = Date.now();
      }
    };

    const onRejected = async ({ from }) => {
      if (from === peerInfo.id) {
        endCall(false);
      }
    };

    socket.on("receive-signal", onReceiveSignal);
    socket.on("call-accepted", onAccepted);
    socket.on("call-rejected", onRejected);

    return () => {
      cleanupConnections();
      socket.off("receive-signal", onReceiveSignal);
      socket.off("call-accepted", onAccepted);
      socket.off("call-rejected", onRejected);
    };
  }, [socket, peerInfo?.id, userId, navigate]);

  return (
    <div className="tailwind">
      <ActiveCall
        calleeName={peerInfo?.name || (status === "connected" ? "On call" : "Calling...")}
        calleeImage={peerInfo?.image}
        status={status}
        startTimestamp={callStartTs}
        onEndCall={endCall}
        localStream={localStream}
        remoteStream={remoteStream}
        initialVideo={initialVideo}
        onSwapCamera={swapCamera}
        localFacing={localFacing}
      />
    </div>
  );
}